import { computed, Ref } from 'vue';
import { UtilizationPrice } from '@/api';
import { dynPricingDayOfWeek, fullCalendarDayOfWeek } from '@/utils/dateUtils';

interface DailyUtilizationGroup {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  utilizations: UtilizationPrice[];
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  extendedProps: {
    pricePercentage?: number | null;
    priceAmount?: number | null;
    usePercent?: boolean;
    dayOfWeek: number;
    utilizationRanges: UtilizationPrice[];
  };
}

export function useCalendarDataTransform(
  workingData: Ref<UtilizationPrice[]>,
  mondayDate: Date,
  lastTempId: Ref<number>
) {
  function groupUtilizationsByDayAndTime(
    utilizationPrices: UtilizationPrice[]
  ): DailyUtilizationGroup[] {
    const groupsMap = utilizationPrices.reduce(
      (
        accumulator: Record<string, UtilizationPrice[]>,
        utilization: UtilizationPrice
      ) => {
        if (utilization.dayOfTheWeek == null) return accumulator;

        const groupKey = [
          fullCalendarDayOfWeek(utilization.dayOfTheWeek),
          utilization.startTime,
          utilization.endTime,
        ].join('|');

        if (!accumulator[groupKey]) {
          accumulator[groupKey] = [];
        }
        accumulator[groupKey].push(utilization);

        return accumulator;
      },
      {}
    );

    return Object.entries(groupsMap).map(([groupKey, utilizations]) => {
      const [dayOfWeek, startTime, endTime] = groupKey.split('|');
      return {
        id: groupKey,
        dayOfWeek: Number(dayOfWeek),
        startTime,
        endTime,
        utilizations,
      };
    });
  }

  function transformToCalendarEvents(
    utilizationPrices: UtilizationPrice[]
  ): CalendarEvent[] {
    const safeData = Array.isArray(utilizationPrices) ? utilizationPrices : [];
    const groupedUtilizations = groupUtilizationsByDayAndTime(safeData);

    return groupedUtilizations.map((group) => {
      const eventDate = calculateDateForDayOfWeek(mondayDate, group.dayOfWeek);
      const eventStartTime = extractTimeFromString(group.startTime);
      const eventEndTime = extractTimeFromString(group.endTime);
      const eventId = generateEventId(group.id, lastTempId);

      return {
        id: eventId,
        title: group.utilizations[0].timeWindowDescription ?? '',
        start: `${formatDate(eventDate)}T${eventStartTime}`,
        end: `${formatDate(eventDate)}T${eventEndTime}`,
        extendedProps: {
          pricePercentage: group.utilizations[0].pricePercentage,
          priceAmount: group.utilizations[0].priceAmount,
          usePercent: group.utilizations[0].usePercent,
          dayOfWeek: fullCalendarDayOfWeek(group.dayOfWeek),
          utilizationRanges: group.utilizations.map((util) => ({
            ...JSON.parse(JSON.stringify(util)),
            dayOfTheWeek: fullCalendarDayOfWeek(util.dayOfTheWeek ?? 0),
          })),
        },
      };
    });
  }

  function calculateDateForDayOfWeek(monday: Date, dayOfWeek: number): Date {
    const date = new Date(monday);
    date.setDate(monday.getDate() + dynPricingDayOfWeek(dayOfWeek) - 1);
    return date;
  }

  function extractTimeFromString(timeString?: string): string {
    if (!timeString) return '00:00:00';
    const parts = timeString.split('T');
    return parts.length > 1 ? parts[1] : '00:00:00';
  }

  function generateEventId(
    groupId: string,
    lastTempIdRef: Ref<number>
  ): string {
    return groupId && groupId !== '0'
      ? groupId.toString()
      : `temp_${lastTempIdRef.value--}`;
  }

  function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const calendarEvents = computed(() =>
    transformToCalendarEvents(workingData.value)
  );

  return {
    calendarEvents,
    transformToCalendarEvents,
    groupUtilizationsByDayAndTime,
    formatDate,
  };
}
